import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  Link
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../../services/useApi';
import CtosModal from './CtosModal';

interface DiscoveredOnu {
  sfp: string;
  onu_id: number;
  gpon_sn: string;
}

interface NotProvisionedTabProps {
  oltId?: string;
}

const NotProvisionedTab: React.FC<NotProvisionedTabProps> = ({ oltId }) => {
  const [onus, setOnus] = useState<DiscoveredOnu[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ctosModal, setCtosModal] = useState<{ open: boolean; sfp: string }>({ open: false, sfp: '' });

  const fetchDiscoveredOnus = async () => {
    if (!oltId) {
      setError('ID da OLT não informado');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/olts/${oltId}/onus-not-provisioned`);
      const data = response.data?.data ?? [];
      const list = Array.isArray(data) ? data : [];
      setOnus(list);
      if (!response.data?.success && response.data?.message) {
        setError(response.data.message);
      }
    } catch (err: any) {
      console.error('Erro ao buscar ONUs não provisionadas:', err);
      const msg = err.response?.data?.message || err.message || 'Erro ao buscar ONUs na OLT';
      setError(msg);
      toast.error(msg);
      setOnus([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (oltId) {
      fetchDiscoveredOnus();
    }
  }, [oltId]);

  if (!oltId) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          ONUs Não Provisionadas
        </Typography>
        <Alert severity="warning">
          ID da OLT não informado. Selecione uma OLT para visualizar as ONUs não provisionadas.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          ONUs Não Provisionadas
        </Typography>
        <Tooltip title="Atualizar lista">
          <IconButton
            onClick={fetchDiscoveredOnus}
            disabled={loading}
            color="primary"
            size="small"
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Buscando ONUs na OLT...
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>SFP</strong></TableCell>
                <TableCell><strong>ONU ID</strong></TableCell>
                <TableCell><strong>GPON SN</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {onus.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      Nenhuma ONU não provisionada encontrada na OLT.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                onus.map((onu, idx) => (
                  <TableRow key={`${onu.sfp}-${onu.gpon_sn}-${idx}`} hover>
                    <TableCell>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => setCtosModal({ open: true, sfp: onu.sfp })}
                        sx={{ cursor: 'pointer', textDecoration: 'underline', fontFamily: 'monospace' }}
                      >
                        {onu.sfp}
                      </Link>
                    </TableCell>
                    <TableCell>{onu.onu_id}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{onu.gpon_sn}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CtosModal
        open={ctosModal.open}
        onClose={() => setCtosModal({ open: false, sfp: '' })}
        oltId={oltId}
        sfp={ctosModal.sfp}
        portNumber={ctosModal.sfp}
      />
    </Box>
  );
};

export default NotProvisionedTab;
